import {
  discardExpiredFailures,
  discardMutationsOfOtherUsers,
  hasOlderUnsentMutation,
  listPendingMutations,
  markFailed,
  markRetryable,
  markSending,
  requeueUnconfirmedMutations,
  markAwaiting,
} from "./pendingMutationsDb.ts";
import { apiFetch, ApiError } from "../Api/apiFetch.ts";
import { isTooOldToResend } from "./pendingMutationRules.ts";
import { showSnackbar } from "../Components/UI/Snackbar/showSnackbar.ts";
import { OfflineQueueResources } from "../Resources/OfflineQueueResources.ts";
import { pickResource } from "../Resources/pickResource.ts";
import type { OfflineQueueStore } from "./OfflineQueueStore.ts";

const MAX_RETRIES = 5;
const STATUS_RECOVERABLE_BY_TOKEN_REFRESH = 401;

export function isTerminalFailure(error: unknown, retries: number): boolean {
  if (retries >= MAX_RETRIES) return true;
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== STATUS_RECOVERABLE_BY_TOKEN_REFRESH
  );
}

let flushInFlight = false;
let flushRequestedAgain = false;
let notifyTerminalFailure: () => void = () => {};
let currentUserUuid: () => string | undefined = () => undefined;

export function configureOfflineQueueUser(get: () => string | undefined): void {
  currentUserUuid = get;
}

export function configureOfflineQueueFailureNotice(notify: () => void): void {
  notifyTerminalFailure = notify;
}

function reportQueueStartupFailure(error: unknown): void {
  console.error("[offline-queue] could not resume pending mutations:", error);
}

export async function flushPendingMutations(): Promise<void> {
  if (flushInFlight) {
    flushRequestedAgain = true;
    return;
  }
  flushInFlight = true;
  try {
    // A pass works from a snapshot, so anything that arrived or became unblocked during it needs
    // another one. The early returns below end a pass, not the flush.
    do {
      flushRequestedAgain = false;
      await runFlushPass();
    } while (flushRequestedAgain);
  } finally {
    flushInFlight = false;
  }
}

async function runFlushPass(): Promise<void> {
  const userUuid = currentUserUuid();
  if (!userUuid) return;
  await discardMutationsOfOtherUsers(userUuid);
  await discardExpiredFailures();
  const entries = await listPendingMutations();
  const now = Date.now();
  for (const entry of entries) {
    // A direct send may have an older entry for this endpoint in flight; overtaking it here would
    // let the older value land last. It gets its turn once that send resolves.
    if (await hasOlderUnsentMutation(entry)) continue;
    if (isTooOldToResend(entry, now)) {
      await markFailed(entry.operationUuid, entry.retries);
      notifyTerminalFailure();
      continue;
    }
    await markSending(entry.operationUuid);
    try {
      await apiFetch(entry.endpoint, {
        method: entry.method,
        body: entry.body,
      });
      await markAwaiting(entry.operationUuid);
    } catch (err) {
      const reachedServer = err instanceof ApiError;
      const retries = reachedServer ? entry.retries + 1 : entry.retries;
      if (isTerminalFailure(err, retries)) {
        await markFailed(entry.operationUuid, retries);
        notifyTerminalFailure();
      } else {
        await markRetryable(entry.operationUuid, retries);
        // Stop the pass: a later entry overtaking this one would let an older value win.
        return;
      }
    }
  }
}

export function startPendingMutationsWatcher(store: OfflineQueueStore): () => void {
  configureOfflineQueueUser(() => store.getState().auth.user?.sub);
  configureOfflineQueueFailureNotice(() =>
    showSnackbar(
      store.dispatch,
      pickResource(OfflineQueueResources, store.getState().core.language).submitFailed,
    ),
  );

  const flushIfOnline = (): void => {
    if (navigator.onLine) void flushPendingMutations();
  };
  const onOnline = (): void => {
    void flushPendingMutations();
  };
  // focus is not raised reliably when a PWA returns from the background on mobile.
  const onVisibilityChange = (): void => {
    if (document.visibilityState === "visible") flushIfOnline();
  };

  window.addEventListener("online", onOnline);
  window.addEventListener("focus", flushIfOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);

  // The watcher starts before the user is known: auth is not persisted and arrives from an async
  // request, so the first flush would find no owner and bail with nothing to retry it.
  let knownUserUuid = store.getState().auth.user?.sub;
  const unsubscribe = store.subscribe(() => {
    const userUuid = store.getState().auth.user?.sub;
    if (userUuid === knownUserUuid) return;
    knownUserUuid = userUuid;
    if (userUuid) flushIfOnline();
  });

  void requeueUnconfirmedMutations().then(flushIfOnline).catch(reportQueueStartupFailure);

  return () => {
    unsubscribe();
    window.removeEventListener("online", onOnline);
    window.removeEventListener("focus", flushIfOnline);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
